import { FormatValues } from "@outschool/business-rules";
import { SearchFilters, SearchUseCase } from "@outschool/gql-backend-generated";
import { EAST_ASIA_LOCALES, useLocale } from "@outschool/localization";
import { DAY_END_HOUR, DAY_START_HOUR } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { useSearchQuery } from "@outschool/ui-components-website";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import { usePreviousValue } from "@outschool/ui-utils";
import omit from "lodash/omit";
import React from "react";

const pages_require_reviews = [
  "CURATED_CATEGORY",
  "TUTORING_LANDING_PAGE",
  "BASIC_CATEGORY",
  "BASIC_SCHOOL"
];

export const cleanPayload = (state: SearchFilters, payload: SearchFilters) => {
  const {
    pricePerMeetingMin,
    pricePerMeetingMax,
    startAfterTime,
    endByTime,
    ...filteredPayload
  } = payload;

  let filteredState = { ...state };
  let price = {};

  if (pricePerMeetingMin !== undefined || pricePerMeetingMax !== undefined) {
    filteredState = omit(filteredState, [
      "pricePerMeetingMin",
      "pricePerMeetingMax"
    ]);

    if (!!pricePerMeetingMax) {
      price = { pricePerMeetingMax };
    } else if (!!pricePerMeetingMin) {
      price = { pricePerMeetingMin };
    } else {
      price = {};
    }
  }

  let start = null;
  let end = null;

  if (startAfterTime !== undefined || endByTime !== undefined) {
    filteredState = omit(filteredState, ["startAfterTime", "endByTime"]);
    // Without this, if you drag the start/end time to the max values (6-22) it will still
    // show like the filter button is "active" (blue background), even with the default values.
    // So we need to set it to null when it's the default time range
    // @ts-ignore TS(2322): Type '{ startAfterTime: InputMaybe<number> | undef... Remove this comment to see the full error message
    start = startAfterTime !== DAY_START_HOUR ? { startAfterTime } : null;
    // @ts-ignore TS(2322): Type '{ endByTime: InputMaybe<number> | undefined;... Remove this comment to see the full error message
    end = endByTime !== DAY_END_HOUR ? { endByTime } : null;
  }

  return {
    ...filteredState,
    ...filteredPayload,
    ...price,
    // @ts-ignore TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
    ...start,
    // @ts-ignore TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
    ...end,
    includeInProgressFixedLengthLiveFormat:
      filteredPayload.includeInProgressFixedLengthLiveFormat ?? true
  };
};

export const useListingsData = ({
  categorySlug,
  skip = false,
  predefinedCategoryFilters,
  trackSearchFiltersApplied,
  pageLocation,
  usePromotedRanking,
  useCase,
  skipOtherListings = true
}: {
  categorySlug: string;
  skip?: boolean;
  predefinedCategoryFilters?: SearchFilters;
  trackSearchFiltersApplied(args: {
    filterCategory?: string;
    oldFilters: SearchFilters;
    newFilters: SearchFilters;
  }): void;
  pageLocation: string;
  usePromotedRanking: boolean;
  useCase: SearchUseCase;
  skipOtherListings?: boolean;
}) => {
  const { loggedOutUserExperimentUid: anonymousId } =
    useServerExperimentContext();
  const { userTimeZone, currentUserHasLoaded, isEsaSession } = useSession();
  const [searchFilters, setSearchFilters] = React.useState<SearchFilters>({});

  const updateSearchFilters = React.useCallback(
    (payload: SearchFilters) => {
      setSearchFilters(cleanPayload(searchFilters, payload));
    },
    [setSearchFilters, searchFilters]
  );

  const locale = useLocale();
  const isEAUser = EAST_ASIA_LOCALES.includes(locale);
  const showOnlyRecurringTimes =
    currentUserHasLoaded && !isEsaSession && !isEAUser;

  const combinedFilters: SearchFilters = React.useMemo(() => {
    let additionalFilters = {};
    // only for 1 on 1 classes
    const shouldShowRecurringOnly =
      showOnlyRecurringTimes &&
      (searchFilters?.capacityMax ?? predefinedCategoryFilters?.capacityMax) ===
        1 &&
      (searchFilters?.format ?? predefinedCategoryFilters?.format) ===
        FormatValues.Live;
    if (shouldShowRecurringOnly) {
      additionalFilters = { excludeNonOngoingOneOnOne: true };
    }
    const combinedObj = omit(
      {
        ...predefinedCategoryFilters,
        ...searchFilters,
        ...additionalFilters,
        timeZone: predefinedCategoryFilters?.timeZone || userTimeZone
      },
      "__typename"
    );
    const booleanFilters = ["includeInProgressFixedLengthLiveFormat"];
    return Object.keys(combinedObj).reduce((obj, key) => {
      // @ts-ignore
      if (!!combinedObj[key] || booleanFilters.includes(key)) {
        // @ts-ignore
        obj[key] = combinedObj[key];
      }
      return obj;
    }, {});
  }, [
    userTimeZone,
    searchFilters,
    predefinedCategoryFilters,
    showOnlyRecurringTimes
  ]);
  const oldCombinedFilters = usePreviousValue(combinedFilters);

  React.useEffect(() => {
    trackSearchFiltersApplied({
      newFilters: combinedFilters,
      oldFilters: oldCombinedFilters
    });
  }, [oldCombinedFilters, combinedFilters, trackSearchFiltersApplied]);

  const itemsPerPage = 20;

  // tutoring category page uses recent reviews
  const withRecentReviews =
    pages_require_reviews.includes(pageLocation) &&
    combinedFilters.capacityMax === 1;

  const {
    loading,
    exactListings,
    exactTotal,
    hasMore,
    otherListings,
    exactHasMore,
    isFetchingMore,
    fetchMore,
    error
  } = useSearchQuery(
    {
      referrer: document.referrer,
      skip: skip || !currentUserHasLoaded,
      notifyOnNetworkStatusChange: true,
      variables: {
        searchFilters: combinedFilters,
        offset: 0,
        limit: itemsPerPage,
        skipRating: false,
        withRecentReviews,
        promotedRankingsEnabled: usePromotedRanking,
        anonymousId,
        matchNames: false,
        pageLocation,
        useCase
      }
    },
    skipOtherListings
  );

  return {
    category: categorySlug,
    error,
    exactHasMore,
    exactTotal,
    fetchMore,
    hasMore,
    isFetchingMore,
    listings: exactListings,
    otherListings,
    loading,
    updateSearchFilters,
    searchFilters: combinedFilters
  };
};
